import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const IngredientSourcing = () => {
  const [selectedSource, setSelectedSource] = useState('local');

  const sourcingCategories = [
    { id: 'local', name: 'Local Farms', icon: 'Tractor', color: 'trust' },
    { id: 'mediterranean', name: 'Mediterranean Imports', icon: 'Ship', color: 'primary' },
    { id: 'spices', name: 'Spice Markets', icon: 'Sparkles', color: 'accent' },
    { id: 'organic', name: 'Organic Suppliers', icon: 'Leaf', color: 'success' }
  ];

  const sourcingData = {
    local: {
      title: "Local Farm Partnerships",
      description: "We work directly with local farms within a 50-mile radius to source the freshest vegetables, herbs, and dairy products. These partnerships ensure peak freshness while supporting our local agricultural community.",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      suppliers: [
        {
          name: "Green Valley Organic Farm",
          location: "25 miles north",
          products: "Tomatoes, Cucumbers, Bell Peppers, Fresh Herbs",
          partnership: "Since 2015",
          image: "https://images.unsplash.com/photo-1592419044706-39796d40f98c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Mountain View Dairy",
          location: "18 miles east",
          products: "Fresh Yogurt, Cheese, Organic Milk",
          partnership: "Since 2012",
          image: "https://images.unsplash.com/photo-1628088062854-d1870b4553da?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Riverside Herb Gardens",
          location: "12 miles south",
          products: "Mint, Parsley, Cilantro, Dill, Oregano",
          partnership: "Since 2018",
          image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        }
      ]
    },
    mediterranean: {
      title: "Authentic Mediterranean Imports",
      description: "For ingredients that define authentic Mediterranean cuisine, we import directly from trusted suppliers across Turkey, Greece, Lebanon, and Morocco. These specialty items maintain the traditional flavors our recipes demand.",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      suppliers: [
        {
          name: "Aegean Olive Company",
          location: "Izmir, Turkey",
          products: "Extra Virgin Olive Oil, Kalamata Olives, Olive Tapenade",
          partnership: "Since 2010",
          image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Damascus Spice Merchants",
          location: "Aleppo, Syria",
          products: "Sumac, Za\'atar, Aleppo Pepper, Baharat Blend",
          partnership: "Since 2008",
          image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Cretan Cheese Artisans",
          location: "Crete, Greece",
          products: "Feta Cheese, Kasseri, Graviera",
          partnership: "Since 2014",
          image: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        }
      ]
    },
    spices: {
      title: "Traditional Spice Markets",
      description: "Our spice blends are the heart of our authentic flavors. We source premium spices from traditional markets and specialized growers who have perfected their craft over generations.",
      image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      suppliers: [
        {
          name: "Istanbul Grand Bazaar Spices",
          location: "Istanbul, Turkey",
          products: "Cumin, Coriander, Paprika, Turkish Red Pepper",
          partnership: "Since 2005",
          image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Gaziantep Pistachio Cooperative",
          location: "Gaziantep, Turkey",
          products: "Antep Pistachios, Pistachio Oil, Pistachio Paste",
          partnership: "Since 2011",
          image: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Lebanese Mountain Herbs",
          location: "Mount Lebanon, Lebanon",
          products: "Wild Thyme, Mountain Oregano, Lebanese Seven Spice",
          partnership: "Since 2016",
          image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        }
      ]
    },
    organic: {
      title: "Certified Organic Suppliers",
      description: "For health-conscious customers and premium dishes, we maintain partnerships with certified organic suppliers who share our commitment to quality and sustainability.",
      image: "https://images.unsplash.com/photo-1560493676-04071c5f467b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      suppliers: [
        {
          name: "Pure Earth Organics",
          location: "California, USA",
          products: "Organic Quinoa, Ancient Grains, Organic Legumes",
          partnership: "Since 2019",
          image: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Sustainable Seas Collective",
          location: "Mediterranean Coast",
          products: "Wild-Caught Fish, Sustainable Seafood",
          partnership: "Since 2017",
          image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        },
        {
          name: "Highland Organic Meats",
          location: "Scotland",
          products: "Grass-Fed Lamb, Organic Beef, Free-Range Chicken",
          partnership: "Since 2020",
          image: "https://images.unsplash.com/photo-1588417936104-c4b4112c8fc8?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
        }
      ]
    }
  };

  const currentData = sourcingData[selectedSource];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Quality Ingredient Sourcing
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Every authentic flavor starts with premium ingredients. We've built trusted relationships with suppliers who share our commitment to quality, authenticity, and sustainability.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {sourcingCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedSource(category.id)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-cta font-semibold transition-organic ${
              selectedSource === category.id
                ? `bg-${category.color} text-white shadow-warm`
                : `text-${category.color} hover:bg-${category.color}-50 border border-${category.color}-200`
            }`}
          >
            <Icon name={category.icon} size={20} />
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Interactive Map Placeholder */}
      <motion.div
        key={selectedSource}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12"
      >
        <div className="card-surface">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2">
              <h3 className="font-headline text-3xl font-bold text-text-primary mb-4">
                {currentData.title}
              </h3>
              <p className="font-body text-text-secondary leading-relaxed mb-6">
                {currentData.description}
              </p>
              
              {/* Interactive Map */}
              <div className="relative bg-primary-50 rounded-lg overflow-hidden h-64">
                <Image
                  src={currentData.image}
                  alt={currentData.title}
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 to-transparent flex items-end justify-center p-6">
                  <div className="text-center text-white">
                    <Icon name="MapPin" size={32} className="mx-auto mb-2" />
                    <p className="font-cta font-semibold">
                      {currentData.suppliers.length} Trusted Partners
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2">
              <h4 className="font-cta text-xl font-semibold text-text-primary mb-6 flex items-center space-x-2">
                <Icon name="Users" size={20} />
                <span>Our Trusted Partners</span>
              </h4>
              
              <div className="space-y-4">
                {currentData.suppliers.map((supplier, index) => (
                  <motion.div
                    key={supplier.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-surface rounded-lg p-4 hover:shadow-warm transition-organic"
                  >
                    <div className="flex gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          src={supplier.image}
                          alt={supplier.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-cta font-semibold text-text-primary mb-1">
                          {supplier.name}
                        </h5>
                        <div className="flex items-center space-x-1 text-sm text-text-secondary mb-2">
                          <Icon name="MapPin" size={14} />
                          <span>{supplier.location}</span>
                          <span className="mx-2">•</span>
                          <span className="text-accent-600 font-medium">{supplier.partnership}</span>
                        </div>
                        <p className="text-sm text-text-secondary">
                          {supplier.products}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quality Commitment */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-trust-50 to-success-50">
          <Icon name="Shield" size={48} className="text-trust mx-auto mb-6" />
          <h3 className="font-headline text-3xl font-bold text-text-primary mb-6">
            Our Quality Promise
          </h3>
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
            Every ingredient that enters our kitchen meets our strict quality standards. We personally visit our suppliers, verify their practices, and maintain long-term relationships built on trust and shared values of excellence.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-trust rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Eye" size={24} className="text-white" />
              </div>
              <h4 className="font-cta font-semibold text-text-primary mb-2">
                Personal Inspection
              </h4>
              <p className="text-sm text-text-secondary">
                We personally visit and inspect all our suppliers
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={24} className="text-white" />
              </div>
              <h4 className="font-cta font-semibold text-text-primary mb-2">
                Quality Certification
              </h4>
              <p className="text-sm text-text-secondary">
                All suppliers meet our strict quality standards
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Heart" size={24} className="text-white" />
              </div>
              <h4 className="font-cta font-semibold text-text-primary mb-2">
                Long-term Partnerships
              </h4>
              <p className="text-sm text-text-secondary">
                Building relationships based on trust and quality
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default IngredientSourcing;